import { FormEvent, useEffect, useMemo, useState } from "react";
import { appPublicUrl } from "../lib/firebase";
import { adminRouteParams, replaceAdminRouteParams } from "../lib/hashRoute";
import { useAuth } from "../context/AuthContext";
import { AccountMenu } from "./AccountMenu";
import { FormationWorkspace } from "./FormationWorkspace";
import { SessionPreparationWorkspace } from "./SessionPreparationWorkspace";
import { SessionSlideshowWorkspace } from "./SessionSlideshowWorkspace";
import {
  createAdminInvitation,
  listAdminInvitations,
} from "../services/adminAccountService";
import {
  listCurrentUserFormations,
  listCurrentUserSessions,
} from "../services/accessProjectionService";
import {
  listAuditEvents,
  writeAuditEvent,
  type AuditEventRecord,
  type AuditMetadata,
} from "../services/auditService";
import type { UserFormationIndex, UserSessionIndex } from "../types/access";
import type {
  AdminCapabilities,
  AdminInvitation,
  AdminRole,
} from "../types/auth";
import { defaultCapabilitiesForRole, normalizeEmail } from "../utils/firestoreData";

const roleLabels: Record<AdminRole, string> = {
  global_admin: "Administrateur global",
  formation_admin: "Administrateur formation",
  trainer: "Formateur",
};

type AdminWorkspace = "overview" | "formations" | "sessions" | "live" | "support";

type AdminWorkspaceItem = {
  id: AdminWorkspace;
  label: string;
  summary: string;
};

const baseWorkspaces: AdminWorkspaceItem[] = [
  {
    id: "overview",
    label: "Pilotage",
    summary: "Compte, accès et travaux récents",
  },
  {
    id: "formations",
    label: "Formations",
    summary: "Catalogue, contenus, versions",
  },
  {
    id: "sessions",
    label: "Sessions",
    summary: "Préparation, participants, livrables",
  },
  {
    id: "live",
    label: "Animation",
    summary: "Slideshow et runtime formateur",
  },
];

const supportWorkspace: AdminWorkspaceItem = {
  id: "support",
  label: "Support",
  summary: "Audit et comptes administrateurs",
};

const adminWorkspaceIds: AdminWorkspace[] = [
  "overview",
  "formations",
  "sessions",
  "live",
  "support",
];

function readAdminWorkspaceParam(): AdminWorkspace {
  const workspace = adminRouteParams().get("workspace");

  return adminWorkspaceIds.includes(workspace as AdminWorkspace)
    ? (workspace as AdminWorkspace)
    : "overview";
}

function formatDate(value: Date | null): string {
  if (!value) {
    return "Non disponible";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
}

function buildInvitationUrl(invitationId: string): string {
  const url = new URL(appPublicUrl);
  url.hash = `/accept-invitation?invite=${encodeURIComponent(invitationId)}`;
  return url.toString();
}

function canManageAdmins(
  isGlobalAdmin: boolean,
  capabilities: AdminCapabilities | undefined,
): boolean {
  return isGlobalAdmin || capabilities?.canManageAdmins === true;
}

function formatAuditMetadata(metadata: AuditMetadata): string {
  const entries = Object.entries(metadata);

  if (entries.length === 0) {
    return "Aucun payload";
  }

  return entries
    .map(([key, value]) => `${key}: ${value === null ? "null" : String(value)}`)
    .join(" - ");
}

export function AdminDashboard() {
  const { isGlobalAdmin, profile, user } = useAuth();
  const [activeWorkspace, setActiveWorkspace] =
    useState<AdminWorkspace>(readAdminWorkspaceParam);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [auditEvents, setAuditEvents] = useState<AuditEventRecord[]>([]);
  const [invitations, setInvitations] = useState<AdminInvitation[]>([]);
  const [formationIndexes, setFormationIndexes] = useState<UserFormationIndex[]>(
    [],
  );
  const [isCreating, setIsCreating] = useState(false);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);
  const [isLoadingIndexes, setIsLoadingIndexes] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [role, setRole] = useState<AdminRole>("formation_admin");
  const [sessionIndexes, setSessionIndexes] = useState<UserSessionIndex[]>([]);

  const capabilities = useMemo(() => defaultCapabilitiesForRole(role), [role]);
  const mayManageAdmins = canManageAdmins(isGlobalAdmin, profile?.capabilities);
  const workspaces = useMemo(
    () =>
      mayManageAdmins ? [...baseWorkspaces, supportWorkspace] : baseWorkspaces,
    [mayManageAdmins],
  );

  useEffect(() => {
    function handleHashChange() {
      const nextWorkspace = readAdminWorkspaceParam();

      setActiveWorkspace(
        nextWorkspace === "support" && !mayManageAdmins ? "overview" : nextWorkspace,
      );
    }

    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("popstate", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("popstate", handleHashChange);
    };
  }, [mayManageAdmins]);

  function openWorkspace(workspace: AdminWorkspace) {
    setActiveWorkspace(workspace);
    replaceAdminRouteParams({ workspace });
  }

  useEffect(() => {
    if (!mayManageAdmins) {
      setAuditEvents([]);
      return;
    }

    setIsLoadingAudit(true);
    setIsLoadingInvitations(true);
    void Promise.all([listAdminInvitations(), listAuditEvents()])
      .then(([nextInvitations, nextAuditEvents]) => {
        setInvitations(nextInvitations);
        setAuditEvents(nextAuditEvents);
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger l'administration support.");
      })
      .finally(() => {
        setIsLoadingAudit(false);
        setIsLoadingInvitations(false);
      });
  }, [mayManageAdmins]);

  async function handleRefreshAudit() {
    if (!mayManageAdmins) {
      return;
    }

    setIsLoadingAudit(true);

    try {
      setAuditEvents(await listAuditEvents());
    } catch (error) {
      console.error(error);
      setMessage("Impossible de rafraîchir le journal d'audit.");
    } finally {
      setIsLoadingAudit(false);
    }
  }

  useEffect(() => {
    if (!user) {
      setFormationIndexes([]);
      setSessionIndexes([]);
      return;
    }

    setIsLoadingIndexes(true);
    void Promise.all([
      listCurrentUserSessions(user.uid),
      listCurrentUserFormations(user.uid),
    ])
      .then(([nextSessions, nextFormations]) => {
        setSessionIndexes(nextSessions);
        setFormationIndexes(nextFormations);
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger les index utilisateur.");
      })
      .finally(() => setIsLoadingIndexes(false));
  }, [user]);

  async function handleCreateInvitation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!user || !mayManageAdmins) {
      return;
    }

    setIsCreating(true);
    setMessage(null);

    try {
      const invitation = await createAdminInvitation({
        actorUid: user.uid,
        draft: {
          capabilities,
          displayName,
          email: normalizeEmail(email),
          role,
        },
      });

      await writeAuditEvent({
        actorUid: user.uid,
        eventType: "admin.invitation.created",
        metadata: {
          invitationId: invitation.id,
          invitedEmail: invitation.email,
          role: invitation.role,
        },
      });

      setInvitations((current) => [invitation, ...current]);
      setDisplayName("");
      setEmail("");
      setRole("formation_admin");
      setMessage("Invitation créée. Le lien peut être transmis au compte invité.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible de créer l'invitation.");
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">Socle administrateur</p>
          <h1>Qorelia Formation</h1>
        </div>
        <AccountMenu />
      </header>

      <nav className="admin-workspace-nav" aria-label="Espaces de travail">
        {workspaces.map((workspace) => (
          <button
            className={
              workspace.id === activeWorkspace
                ? "workspace-tab workspace-tab-active"
                : "workspace-tab"
            }
            key={workspace.id}
            onClick={() => openWorkspace(workspace.id)}
            type="button"
          >
            <span>{workspace.label}</span>
            <small>{workspace.summary}</small>
          </button>
        ))}
      </nav>

      {activeWorkspace === "overview" ? (
        <section className="admin-overview">
          <article className="panel panel-wide">
            <p className="eyebrow">Accès rapides</p>
            <h2>Espaces de travail</h2>
            <div className="workspace-card-grid">
              {workspaces
                .filter((workspace) => workspace.id !== "overview")
                .map((workspace) => (
                  <button
                    className="workspace-card"
                    key={workspace.id}
                    onClick={() => openWorkspace(workspace.id)}
                    type="button"
                  >
                    <strong>{workspace.label}</strong>
                    <span>{workspace.summary}</span>
                  </button>
                ))}
            </div>
          </article>

          <article className="panel">
            <p className="eyebrow">Sessions accessibles</p>
            <h2>{sessionIndexes.length}</h2>
            {isLoadingIndexes ? <p>Chargement des sessions...</p> : null}
            {sessionIndexes.length > 0 ? (
              <ul className="resource-list">
                {sessionIndexes.slice(0, 5).map((sessionIndex) => (
                  <li key={sessionIndex.sessionId}>
                    <strong>{sessionIndex.title}</strong>
                    <span>
                      {sessionIndex.role} - {sessionIndex.status}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p>Aucune session indexée pour ce compte.</p>
            )}
          </article>

          <article className="panel">
            <p className="eyebrow">Formations accessibles</p>
            <h2>{formationIndexes.length}</h2>
            {isLoadingIndexes ? <p>Chargement des formations...</p> : null}
            {formationIndexes.length > 0 ? (
              <ul className="resource-list">
                {formationIndexes.slice(0, 5).map((formationIndex) => (
                  <li key={formationIndex.formationId}>
                    <strong>{formationIndex.title}</strong>
                    <span>{formationIndex.role}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p>Aucune formation indexée pour ce compte.</p>
            )}
          </article>
      </section>
      ) : null}

      {activeWorkspace === "formations" ? <FormationWorkspace /> : null}

      {activeWorkspace === "sessions" ? <SessionPreparationWorkspace /> : null}

      {activeWorkspace === "live" ? <SessionSlideshowWorkspace /> : null}

      {activeWorkspace === "support" && mayManageAdmins ? (
        <section className="management-layout">
          <article className="panel panel-wide">
            <div className="section-heading">
              <div>
                <p className="eyebrow">Audit support</p>
                <h2>Journal des actions sensibles</h2>
              </div>
              <button
                className="button button-secondary"
                disabled={isLoadingAudit}
                onClick={handleRefreshAudit}
                type="button"
              >
                Rafraîchir
              </button>
            </div>
            {isLoadingAudit ? <p>Chargement du journal...</p> : null}
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Événement</th>
                    <th>Acteur</th>
                    <th>Payload</th>
                  </tr>
                </thead>
                <tbody>
                  {auditEvents.map((event) => (
                    <tr key={event.id}>
                      <td>{formatDate(event.createdAt)}</td>
                      <td>{event.eventType}</td>
                      <td>{event.actorUid ?? "Système"}</td>
                      <td>{formatAuditMetadata(event.metadata)}</td>
                    </tr>
                  ))}
                  {auditEvents.length === 0 ? (
                    <tr>
                      <td colSpan={4}>Aucun événement disponible.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </article>

          <article className="panel">
            <p className="eyebrow">Invitation admin</p>
            <h2>Inviter un administrateur</h2>
            <form className="stack" onSubmit={handleCreateInvitation}>
              <label className="field">
                <span>Email</span>
                <input
                  autoComplete="email"
                  inputMode="email"
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  type="email"
                  value={email}
                />
              </label>
              <label className="field">
                <span>Nom affiché</span>
                <input
                  autoComplete="name"
                  onChange={(event) => setDisplayName(event.target.value)}
                  type="text"
                  value={displayName}
                />
              </label>
              <label className="field">
                <span>Rôle</span>
                <select
                  onChange={(event) => setRole(event.target.value as AdminRole)}
                  value={role}
                >
                  <option value="formation_admin">Administrateur formation</option>
                  <option value="trainer">Formateur</option>
                  <option value="global_admin">Administrateur global</option>
                </select>
              </label>
              <div className="capability-strip" aria-label="Capacités attribuées">
                {capabilities.canCreateFormation ? <span>Créer formation</span> : null}
                {capabilities.canCreateSession ? <span>Créer session</span> : null}
                {capabilities.canInviteAdmin ? <span>Inviter admin</span> : null}
                {capabilities.canManageAdmins ? <span>Gérer admins</span> : null}
                {!Object.values(capabilities).some(Boolean) ? <span>Runtime formateur</span> : null}
              </div>
              <button
                className="button button-primary"
                disabled={isCreating}
                type="submit"
              >
                {isCreating ? "Création..." : "Créer l'invitation"}
              </button>
            </form>
            {message ? (
              <div className="alert alert-info" role="status">
                {message}
              </div>
            ) : null}
          </article>

          <article className="panel panel-wide">
            <p className="eyebrow">Invitations</p>
            <h2>Comptes administrateurs invités</h2>
            {isLoadingInvitations ? <p>Chargement des invitations...</p> : null}
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Rôle</th>
                    <th>Statut</th>
                    <th>Lien</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((invitation) => (
                    <tr key={invitation.id}>
                      <td>{invitation.email}</td>
                      <td>{roleLabels[invitation.role]}</td>
                      <td>
                        <span className={`status status-${invitation.status}`}>
                          {invitation.status}
                        </span>
                      </td>
                      <td>
                        {invitation.status === "pending" ? (
                          <input
                            aria-label={`Lien d'invitation pour ${invitation.email}`}
                            readOnly
                            value={buildInvitationUrl(invitation.id)}
                          />
                        ) : (
                          "Déjà utilisée"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
        </section>
      ) : null}

      {activeWorkspace === "support" && !mayManageAdmins ? (
        <section className="panel">
          <p className="eyebrow">Administration</p>
          <h2>Invitations réservées</h2>
          <p>
            La gestion des comptes administrateurs nécessite le claim global ou
            la capacité `canManageAdmins`.
          </p>
        </section>
      ) : null}

    </main>
  );
}
